import { Box, Button, Spinner, Text, VStack } from '@chakra-ui/react';
import CustomInputDateTimeRange from '@components/common/inputs/CustomInputDateTimeRange';
import VideoSelector from '@components/replay/export/VideoSelector';
import { useReplayTimelineContext } from '@components/replay/ReplayTimelineContext';
import { DraggablePanel } from '@components/ui/draggable';
import { useDownloadVideo } from '@hooks/download/useDownloadVideo';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { closePopupByMapIdAndType } from '@redux/maps/maps.reducer';
import { useGetAvailableCamerasQuery } from '@services/c2/c2.api';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { DateTimeRange } from '@/types/dateTime.types';
import { PopupTypeEnum } from '@/types/popup.types';
import { SubCameraLiteConfiguration } from '@/types/sensor/sensors';

type Props = {
  position: { x: number; y: number };
};

export default function VideoExport({ position }: Readonly<Props>) {
  const activeSite = useAppSelector(selectActiveSite);

  const { mapId, range } = useReplayTimelineContext();
  const { startDownload } = useDownloadVideo();

  const closePopupByMapAndType = useWithDispatch(closePopupByMapIdAndType);
  const closePopup = useCallback(
    () => closePopupByMapAndType({ mapId, type: PopupTypeEnum.VIDEO_EXPORT }),
    [closePopupByMapAndType, mapId],
  );

  const [dateFrom, setDateFrom] = useState<Date>(range.from);
  const [dateTo, setDateTo] = useState<Date>(range.to);
  const [selectedSubCameras, setSelectedSubCameras] = useState<SubCameraLiteConfiguration[]>([]);

  const { data: availableCameras, isFetching } = useGetAvailableCamerasQuery(
    {
      site: activeSite!.code,
      from: dateFrom.getTime(),
      to: dateTo.getTime(),
    },
    {
      skip: !activeSite || dateFrom.getTime() >= dateTo.getTime(),
      refetchOnMountOrArgChange: true,
    },
  );

  const doDownload = useCallback(
    () =>
      startDownload({
        from: dateFrom,
        to: dateTo,
        cameras: selectedSubCameras,
      }),
    [dateFrom, dateTo, selectedSubCameras, startDownload],
  );

  return (
    <DraggablePanel.Root top={position.y - 300} left={position.x - 630}>
      <DraggablePanel.Header onClose={closePopup}>
        <FormattedMessage id="videoExport.title" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width="550px" padding={2} gap={2}>
        <Text>
          <FormattedMessage id="videoExport.exportRange" />
        </Text>
        <VStack gap={2} alignItems={'center'} paddingX={2}>
          <Box padding={2} width="100%">
            <CustomInputDateTimeRange
              range={range}
              onChange={async (range: DateTimeRange) => {
                setDateFrom(range.from);
                setDateTo(range.to);
              }}
              singleLine={false}
            />
          </Box>
          {availableCameras?.length ? (
            <VStack>
              <Text width="100%" height="20px">
                <FormattedMessage id="videoExport.selection" />
              </Text>

              <VideoSelector availableCameras={availableCameras} setSelectedSubCameras={setSelectedSubCameras} />
            </VStack>
          ) : (
            <Text>
              <FormattedMessage id="videoExport.noStreamAvailable" />
            </Text>
          )}
          <Button
            width="248px"
            loading={isFetching}
            spinner={<Spinner color="neutral.200" />}
            onClick={() => doDownload()}
            disabled={selectedSubCameras.length === 0}
          >
            <FormattedMessage id="videoExport.submit" values={{ count: selectedSubCameras.length }} />
          </Button>
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
